import React from 'react'
import PropTypes from 'prop-types'
import { ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import { Controller } from 'react-hook-form'
import ServiceTypeAvatar from 'components/common/avatar/ServiceTypeAvatar'
import { RECURRENCE } from 'settings/constants/service'

/**
 * Custom Toggle Button Group for Configured Services filters
 */
const FilterToggleButtonGroup = ({
  name,
  control,
  label,
  options,
  defaultValue,
  size = 'small',
  fullWidth = true,
  exclusive = true,
  showServiceTypeAvatar = false,
}) => {
  const theme = useTheme()

  return (
    <>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <ToggleButtonGroup
            {...field}
            exclusive={exclusive}
            aria-label={name}
            fullWidth={fullWidth}
            size={size}
            onChange={(_, newValue) => {
              // For non-exclusive toggle buttons, newValue is an array of all selected values
              if (!exclusive) {
                field.onChange(newValue)
              } else {
                // For exclusive toggle buttons, just use the value directly
                if (newValue !== null) {
                  field.onChange(newValue)
                }
              }
            }}
          >
            {options.map(option => {
              // For non-exclusive toggle buttons, check if the value is in the array
              const isSelected = exclusive ? field.value === option.value : Array.isArray(field.value) && field.value.includes(option.value)

              return (
                <ToggleButton
                  key={`${name}-${option.value}`}
                  value={option.value}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {showServiceTypeAvatar && (
                    <ServiceTypeAvatar
                      serviceType={option.value === RECURRENCE.RECURRING ? RECURRENCE.RECURRING : RECURRENCE.ON_REQUEST}
                      sx={{
                        bgcolor: isSelected ? undefined : theme.palette.action.disabled,
                      }}
                    />
                  )}
                  {option.label}
                </ToggleButton>
              )
            })}
          </ToggleButtonGroup>
        )}
      />
    </>
  )
}

FilterToggleButtonGroup.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.any.isRequired,
    })
  ).isRequired,
  defaultValue: PropTypes.any,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  exclusive: PropTypes.bool,
  showServiceTypeAvatar: PropTypes.bool,
}

export default FilterToggleButtonGroup
